/**
 * La pantalla de una etapa del camino (Fase 12, R-10).
 *
 * Tres zonas, siempre en el mismo orden: lo que ya tienes · el siguiente paso · con quién hablar.
 * Las etapas RECOMIENDAN, no bloquean (Rais, 17-sep): si conviene hacer antes otra, se dice arriba
 * con el botón para seguir igualmente.
 *
 * Lo que el motor todavía no ejecuta solo (informes del editor, beta y estilo; preparar el plan;
 * ejecutar una pasada) llega con la 1.6.3. Mientras tanto la pantalla no lo esconde ni lo finge:
 * lo ofrece por el chat del agente que toca, con el encargo ya escrito.
 */
import Maquetar from "./Maquetar";
import Encargos from "./Encargos";
import Cubierta from "./Cubierta";
import Publicar from "./Publicar";
import { useEffect, useState } from "react";
import Tablero from "./Tablero";
import Pasadas from "./Pasadas";
import PlanDelLibro from "./Plan";
import Corregir from "./Corregir";
import EjecutarPasada from "./EjecutarPasada";
import EjecutarFase from "./EjecutarFase";
import Escenas from "./Escenas";
import { catalogoModelos, comparado, diagnosticoDelLibro, faseDelLibro, documento, dolares, elegirDelSistema, faltanParaElPlan, gasto, hacerRegistro, informeGlobal, informesDelLibro, tiposDeInforme, modelosAsignados, mostrarEnCarpeta, pedirComparado, pedirDiagnostico, pedirInforme, subir } from "../api";
import type { Comparable, DiagnosticoLibro, FasePrevia, Fichero, FichaInforme, Informe as DatosInforme, ModeloCatalogo, PlanLibro, Trabajo } from "../api";
import { ETAPAS, PALABRAS_MAX_LIBRO, caducadoPor, costeDelDiagnostico, costeInforme, dentroDeCarpeta, estadoDiagnostico, lecturaMasCara, wordDelDiagnostico, nombreConVersion, versionDelNombre, nombreDeRuta, palabrasDe } from "../logica";
import type { TipoDiagnostico } from "../logica";
import { notaDelInforme } from "./useCamino";
import type { Caducidad, Etapa as EstadoEtapa, IdEtapa } from "../logica";
import { HABLAR_EN_ETAPA, PREGUNTA_AL_COACH } from "./Barra";
import { useFlujo } from "../flujo";
import AvisoCaduca from "./AvisoCaduca";

const NOMBRE_AGENTE: Record<string, string> = {
  coach: "el coach literario", editor: "el editor de desarrollo", copyeditor: "el corrector", publicar: "Publicación",
};

export function PrimeroElManuscrito({ carpeta, ficheros = [], alDoc, alSubido, alAviso, cambiar = false }: {
  carpeta: string; ficheros?: Fichero[]; alDoc: (ruta: string) => void; alSubido: (f: Fichero) => void; alAviso?: (t: string) => void;
  /** Con un manuscrito ya elegido: la misma caja, pero para cambiarlo. */
  cambiar?: boolean;
}) {
  const [ocupado, setOcupado] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);

  async function elegir() {
    setFallo(null);
    try {
      const { ruta, cancelado } = await elegirDelSistema("documento", carpeta);
      if (cancelado || !ruta) return;
      if (!dentroDeCarpeta(ruta, carpeta)) {
        setFallo("Ese fichero está fuera de la carpeta del proyecto. Súbelo y se copiará dentro; el original no se toca.");
        return;
      }
      alDoc(ruta);
    } catch (e) { setFallo((e as Error).message); }
  }

  async function recibir(f: File | undefined) {
    if (!f) return;
    setOcupado(true); setFallo(null);
    try {
      const conVersion = nombreConVersion(f.name, ficheros.map((x) => x.name));
      const r = await subir(carpeta, f, conVersion ?? undefined);
      // «Un aviso: versionamos el nombre del fichero» (Rais, 21-sep).
      // Solo se avisa si el nombre ha CAMBIADO: decir «lo hemos guardado como X… tu original X no se
      // toca» con el mismo nombre dos veces no dice nada (Rais, 23-sep).
      if (conVersion && r.name !== f.name) {
        alAviso?.(`Hemos guardado tu manuscrito como «${r.name}»: le ponemos el número de versión en el nombre (${versionDelNombre(r.name)}) para que cada pasada cree la siguiente. Tu original, «${f.name}», no se toca.`);
      } else if (r.igual) {
        alAviso?.(`«${r.name}» ya estaba en la carpeta del proyecto: no se ha duplicado, y ahora es el manuscrito con el que trabajas.`);
      }
      alSubido(r);
      alDoc(r.path);
    } catch (e) { setFallo((e as Error).message); } finally { setOcupado(false); }
  }

  return (
    <section className="carta">
      <h2>{cambiar ? "Cambiar de manuscrito" : "Empieza por tu manuscrito"}</h2>
      <div className="caja-subir"
           onDragOver={(e) => e.preventDefault()}
           onDrop={(e) => { e.preventDefault(); recibir(e.dataTransfer.files?.[0]); }}>
        <b>Arrastra aquí tu manuscrito (.docx)</b>
        <span className="vacio">Se copia a la carpeta del proyecto. El original no se toca.</span>
        <div className="botones" style={{ justifyContent: "center" }}>
          <label className="btn" style={{ cursor: ocupado ? "wait" : "pointer" }}>
            {ocupado ? "Subiendo…" : "Subir un manuscrito"}
            <input type="file" hidden accept=".docx,.md,.txt" disabled={ocupado}
                   onChange={(e) => { recibir(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
          <button className="btn fantasma" onClick={elegir}>Elegir de la carpeta del proyecto</button>
        </div>
      </div>
      {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
      <p className="vacio">{cambiar
        ? "Se copia al proyecto con su número de versión y pasa a ser el manuscrito con el que trabajas. Tu original no se toca."
        : "Las cinco etapas trabajan sobre tu manuscrito: el Studio no escribe un libro desde cero."}</p>
    </section>
  );
}

export default function Etapa({ id, etapa, informe, plan, planEnCarpeta, carpeta, doc, ficheros, pulso, alHablar, alIr, trabajos = [], alLanzar, alCambio, planSoportado = false, alCorregir, alSeguirCon, alCambiarDoc, corrigiendo = false }: {
  id: IdEtapa;
  etapa: EstadoEtapa;
  /** Volver a una versión anterior (P-04): el manuscrito pasa a ser la copia nueva, sin más aviso. */
  alCambiarDoc?: (ruta: string) => void;
  informe: DatosInforme | null | undefined;
  plan: PlanLibro | null;
  planEnCarpeta: boolean;
  carpeta: string;
  doc: string | null;
  ficheros: Fichero[];
  pulso: number;
  alHablar: (agente: string, encargo: string) => void;
  alIr: (seccion: string) => void;
  trabajos?: Trabajo[];
  alCambio?: () => void;
  planSoportado?: boolean;
  alCorregir?: (capitulo: { title: string; texto: string } | null) => void;
  corrigiendo?: boolean;
  alLanzar?: () => void;
  /** Cambiar el manuscrito abierto (lo usa la pasada al crear una versión nueva). */
  alSeguirCon?: (ruta: string) => void;
}) {
  const [seguir, setSeguir] = useState(false);
  // EL STUDIO COMO FLUJO (3-oct-2026): un solo interlocutor, el coach; sin tablero ni documentos del libro.
  const flujo = useFlujo();
  const ultimoTrabajo = (tipo: string) => trabajos.find((t) => t.tipo === tipo) ?? null;
  const trabajoInforme = ultimoTrabajo("informe-global");
  const trabajoMedir = ultimoTrabajo("medir");

  // DIAGNÓSTICO (R-14): las fichas de los informes, las palabras del manuscrito y el precio del
  // modelo de cada agente, para decir lo que cuesta ANTES de pedir. `undefined` = motor sin la ruta.
  const [fichas, setFichas] = useState<Record<string, FichaInforme> | null | undefined>(undefined);
  const [palabras, setPalabras] = useState<number | null>(null);
  const [precios, setPrecios] = useState<Record<string, ModeloCatalogo | undefined>>({});
  const [pidiendo, setPidiendo] = useState<string | null>(null);
  const [falloInforme, setFalloInforme] = useState<string | null>(null);
  useEffect(() => {
    if (id !== "diagnostico" || !carpeta) return;
    informesDelLibro(carpeta).then((f) => setFichas(f ?? undefined)).catch(() => setFichas(undefined));
  }, [id, carpeta, pulso]);
  useEffect(() => {
    if (id !== "diagnostico" || !doc) { setPalabras(null); return; }
    documento(doc).then((d) => setPalabras("error" in d ? null : palabrasDe(d.text))).catch(() => setPalabras(null));
  }, [id, doc]);
  useEffect(() => {
    if (id !== "diagnostico" && id !== "pasadas" && id !== "correccion") return;
    Promise.all([modelosAsignados(), catalogoModelos()]).then(([m, c]) => {
      const precio = (ag: string) => c.modelos.find((x) => x.id === (m.porAgente ?? {})[ag]);
      setPrecios({ editor: precio("editor"), coach: precio("coach"), copyeditor: precio("copyeditor") });
    }).catch(() => {});
  }, [id]);

  // EL COMPARADO (21-sep): solo aparece cuando se puede —después de reescribir y volver a leer—.
  // De salida no hay nada con qué comparar, y una fila apagada solo confundiría.
  const [comp, setComp] = useState<Comparable | null>(null);
  const trabajoComparado = ultimoTrabajo("informe-comparado");
  useEffect(() => {
    if (id !== "diagnostico" || !carpeta || !doc) { setComp(null); return; }
    comparado(carpeta, doc).then(setComp).catch(() => setComp(null));
  }, [id, carpeta, doc, pulso, trabajoInforme?.estado]);
  async function pedirElComparado() {
    if (!doc) return;
    setPidiendo("comparado"); setFalloInforme(null);
    try { await pedirComparado(carpeta, doc); alLanzar?.(); }
    catch (e) { setFalloInforme((e as Error).message); }
    finally { setPidiendo(null); }
  }

  // EL PLAN se escribe con TODOS los informes delante (Rais, 21-sep): el motor dice cuáles faltan.
  const [faltan, setFaltan] = useState<{ id: string; titulo: string }[] | null>(null);
  useEffect(() => {
    if (id !== "plan" || !carpeta) { setFaltan(null); return; }
    faltanParaElPlan(carpeta, doc).then(setFaltan).catch(() => setFaltan(null));
  }, [id, carpeta, doc, pulso]);

  // EL REGISTRO de la reescritura: versión a versión, en Word, sin gastar.
  const [registro, setRegistro] = useState<{ ruta?: string; error?: string; haciendo?: boolean } | null>(null);
  async function hacerElRegistro() {
    if (!doc) return;
    setRegistro({ haciendo: true });
    try { const r = await hacerRegistro(carpeta, doc); setRegistro({ ruta: r.ruta }); }
    catch (e) { setRegistro({ error: (e as Error).message }); }
  }

  // EL INFORME DE LECTURA se pide desde su fila, como los otros tres (Rais, 21-sep: «que arriba
  // ponga Pedirlo y quítalo de abajo»). El informe entero se lee en su sección; aquí solo se pide.
  async function pedirLectura() {
    if (!doc) return;
    setPidiendo("lectura"); setFalloInforme(null);
    try {
      const d = await documento(doc);
      if ("error" in d) throw new Error(d.error);
      const otros = ficheros.filter((f) => f.path !== doc).slice(0, 6).map((f) => f.path);
      await informeGlobal(d.text, carpeta, otros, doc);
      alLanzar?.();
    } catch (e) {
      const m = (e as Error).message;
      setFalloInforme(/novela, relatos, ensayo/.test(m)
        ? "Antes de pedirlo, di qué tipo de libro es (novela, ensayo, memorias…) en Proyectos: con eso se lee con la vara que toca."
        : m);
    } finally {
      setPidiendo(null);
    }
  }

  async function pedir(tipo: string) {
    if (!doc) return;
    setPidiendo(tipo); setFalloInforme(null);
    try { await pedirInforme(tipo, carpeta, doc, !!fichas?.[tipo]); alLanzar?.(); }
    catch (e) { setFalloInforme((e as Error).message); }
    finally { setPidiendo(null); }
  }

  // EL DIAGNÓSTICO DE UNA VEZ (3-oct-2026): lo que costó el informe de lectura la última vez en este
  // libro, para poder decir el coste antes; y el botón que lanza todo lo que falta.
  const [lecturaMedida, setLecturaMedida] = useState<number | null>(null);
  useEffect(() => {
    if (id !== "diagnostico" || !flujo || !carpeta) return;
    gasto(carpeta).then((g) => setLecturaMedida(lecturaMasCara(g.operaciones))).catch(() => setLecturaMedida(null));
  }, [id, flujo, carpeta, pulso]);
  // M-2 (motor 1.7): el diagnóstico como UN trabajo —los informes de una lectura, de uno en uno— y su
  // Word único. `undefined`: el motor es anterior; entonces se lanzan desde aquí, como hasta ahora.
  // `"sin_leer"`: el motor no ha contestado bien. NO es «motor anterior»: ahí no se lanza nada.
  const [diag, setDiag] = useState<DiagnosticoLibro | null | undefined | "sin_leer">(undefined);
  const trabajoDiagnostico = ultimoTrabajo("diagnostico");
  useEffect(() => {
    if (id !== "diagnostico" || !flujo || !carpeta || !doc) { setDiag(undefined); return; }
    diagnosticoDelLibro(carpeta, doc).then(setDiag).catch(() => setDiag("sin_leer"));
  }, [id, flujo, carpeta, doc, pulso, trabajoDiagnostico?.estado]);
  // LA FASE ENTERA (M-5): qué fase toca y cuánto va a mandar al modelo, dicho por el motor.
  // `null`: aún no ha contestado. `undefined`: el motor es anterior y hace las pasadas de una en una.
  const [fasePrevia, setFasePrevia] = useState<FasePrevia | null | undefined>(null);
  // El motor no contestó bien y todavía no se sabe nada: no es «motor anterior» ni «mirando» para siempre.
  const [faseSinLeer, setFaseSinLeer] = useState(false);
  const [deUnaEnUna, setDeUnaEnUna] = useState(false);
  const trabajoFase = ultimoTrabajo("fase");
  const leerFase = () => {
    if (id !== "pasadas" || !flujo || !carpeta || !doc) { setFasePrevia(null); setFaseSinLeer(false); return; }
    // Si falla la lectura se conserva lo que había: un corte de red no cambia la pantalla de modo.
    faseDelLibro(carpeta, doc).then((f) => { setFasePrevia(f); setFaseSinLeer(false); }).catch(() => setFaseSinLeer(true));
  };
  useEffect(leerFase, [id, flujo, carpeta, doc, pulso, trabajoFase?.estado]);
  // LAS ESCENAS, PRIMER PASO DEL PLAN (M-3): `true` partido, `false` falta partirlo, `null` no se sabe
  // (motor anterior, o aún sin contestar): solo `false` retiene el botón del plan.
  const [escenasListas, setEscenasListas] = useState<boolean | null>(null);
  /** Juntar en un Word lo que ya está hecho: no llama a ningún modelo y no cuesta nada. */
  async function juntarEnWord() {
    if (!doc) return;
    setPidiendo("word"); setFalloInforme(null);
    try { await pedirDiagnostico(carpeta, doc); alLanzar?.(); }
    catch (e) { setFalloInforme((e as Error).message); }
    finally { setPidiendo(null); }
  }
  async function hacerDiagnostico(faltan: TipoDiagnostico[]) {
    if (!doc || !faltan.length) return;
    setPidiendo("todo"); setFalloInforme(null);
    const fallos: string[] = [];
    // Con el motor nuevo: el informe de lectura por su ruta (va capítulo a capítulo) y, lo demás, un solo
    // pedido que el motor encadena y cierra con el Word. Con uno anterior, de uno en uno desde aquí.
    if (diag === "sin_leer") return;   // sin saber qué motor hay, no se lanza nada (el botón ya no se ofrece)
    const enCadena = diag !== undefined;
    for (const tipo of enCadena ? faltan.filter((t) => t === "lectura") : faltan) {
      try {
        if (tipo === "lectura") {
          const d = await documento(doc);
          if ("error" in d) throw new Error(d.error);
          const otros = ficheros.filter((f) => f.path !== doc).slice(0, 6).map((f) => f.path);
          await informeGlobal(d.text, carpeta, otros, doc);
        } else {
          await pedirInforme(tipo, carpeta, doc, !!fichas?.[tipo]);
        }
        alLanzar?.();
      } catch (e) {
        const m = (e as Error).message;
        // Sin tipo de obra o sin género no se lee nada: se dice una vez y se para, no cinco.
        if (/novela, relatos, ensayo|género/i.test(m)) {
          setFalloInforme("Antes de empezar, di en Proyectos qué tipo de libro es y de qué género: con eso se lee con la vara que toca.");
          setPidiendo(null);
          return;
        }
        fallos.push(m);
      }
    }
    if (enCadena) {
      try { await pedirDiagnostico(carpeta, doc); alLanzar?.(); }
      catch (e) { fallos.push((e as Error).message); }
    }
    if (fallos.length) setFalloInforme(fallos[0]);
    setPidiendo(null);
  }
  // Lo que una versión nueva dejó viejo (Rais, 22-sep): el informe de lectura por su fecha, los
  // otros tres por la suya. El plan hereda la caducidad de los informes con que se escribió.
  const cadLectura = caducadoPor(informe?.at);
  // De qué versión es el informe: tras tres pasadas, el del v01 ya no habla del libro que tienes
  // (Rais, 23-sep: «¿por qué pone volver a pedir?»).
  const leidaOtra = informe?.doc && doc && nombreDeRuta(doc) !== informe.doc ? informe.doc : null;
  const cadFicha = (tipo: string) => caducadoPor(fichas?.[tipo]?.fecha);
  const caduca: Caducidad | null = cadLectura ?? (["editor", "beta", "estilo", "mercado"].map(cadFicha).find(Boolean) ?? null);
  const h = flujo ? { agente: "coach", encargo: PREGUNTA_AL_COACH[id] } : HABLAR_EN_ETAPA[id];
  const nombre = ETAPAS.find((e) => e.id === id)!.nombre;
  const pasadasPlan = (plan?.areas ?? []).flatMap((a) => (a.pasadas ?? []).map((p) => ({ ...p, area: a.nombre })));
  const siguiente = pasadasPlan.find((p) => p.estado !== "hecha" && p.estado !== "saltada");
  const pendientes = pasadasPlan.filter((p) => p.estado !== "hecha" && p.estado !== "saltada");

  const cabecera = (
    <>
      <div className="botones" style={{ justifyContent: "space-between" }}>
        <div className="saludo"><h1>{nombre}</h1></div>
        <button className="btn fantasma" onClick={() => alHablar(h.agente, h.encargo)}>{flujo ? "Preguntar al coach" : `Hablar con ${NOMBRE_AGENTE[h.agente]}`}</button>
      </div>
      {etapa.recomendado && !seguir && (
        <div className="aviso">
          <b>Recomendado antes:</b> {etapa.recomendado}.
          <button className="btn texto" onClick={() => setSeguir(true)}>Seguir de todos modos</button>
        </div>
      )}
    </>
  );

  // El comparado: la misma fila en el Diagnóstico de siempre y en el del flujo (equipo rojo, 3-oct:
  // en el flujo había desaparecido sin otro camino).
  const filaComparado = (comp?.posible || trabajoComparado) ? (() => {
    const enMarcha = trabajoComparado?.estado === "en_curso" || pidiendo === "comparado";
    const ruta = trabajoComparado?.estado === "hecho" ? String(trabajoComparado.resultado?.ruta ?? "") : "";
    return (
      <div className="fila-etapa">
        <div className="que">
          <b>Informe de lectura comparado</b>
          <span className="vacio">
            {enMarcha ? `comparando ${comp?.anterior ?? "la lectura anterior"} con ${comp?.actual ?? "esta"}…`
              : ruta ? `En tu carpeta: ${nombreDeRuta(ruta)}`
              : trabajoComparado?.estado === "fallido" ? `no se pudo terminar: ${trabajoComparado.error}`
              : `${comp?.anterior} → ${comp?.actual} · qué ha cambiado entre las dos lecturas, problema a problema, con las notas lado a lado`}
          </span>
        </div>
        {ruta && <span className="chip-estado ok">hecho</span>}
        {enMarcha
          ? <span className="chip-estado">en marcha</span>
          : comp?.posible && <button className={ruta ? "btn texto" : "btn fantasma"} onClick={pedirElComparado}>{ruta ? "Rehacer" : "Pedirlo"}</button>}
      </div>
    );
  })() : null;

  if (id === "diagnostico" && flujo) {
    const d = estadoDiagnostico({
      doc, informe, notaLectura: notaDelInforme(informe), fichas,
      trabajos: (["diagnostico", "informe-global", "informe-editor", "informe-beta", "informe-estilo", "informe-mercado"]
        .map(ultimoTrabajo).filter(Boolean) as Trabajo[]),
      tipos: fichas === undefined ? null : tiposDeInforme,
    });
    const coste = costeDelDiagnostico(d.faltan, palabras, precios, lecturaMedida);
    const lanzando = pidiendo === "todo";
    const nota = (x: number | null) => (x == null ? "" : `nota ${String(x).replace(".", ",")}`);
    // Lo que el motor lee de un libro largo en los cuatro informes de una sola lectura (equipo rojo,
    // 3-oct: «tu libro entero» no era verdad por encima de ~83.000 palabras). Se dice antes y después.
    const largo = palabras != null && palabras > PALABRAS_MAX_LIBRO;
    const cortos = d.faltan.filter((t) => t !== "lectura").length;
    const soloElPrincipio = `solo las primeras ~${PALABRAS_MAX_LIBRO.toLocaleString("es-ES")} palabras`;
    const word = wordDelDiagnostico(diag === "sin_leer" ? null : diag, doc);
    const cadenaRota = trabajoDiagnostico && (trabajoDiagnostico.estado === "fallido" || trabajoDiagnostico.estado === "interrumpido") && d.faltan.length > 0
      ? trabajoDiagnostico.error ?? "se cortó" : null;
    return (
      <>
        {cabecera}
        <section className="carta">
          <h2>El diagnóstico de tu manuscrito{doc ? <span className="sub">{nombreDeRuta(doc)}</span> : null}</h2>
          <p className="vacio">
            {d.piezas.length === 5 ? "Cinco" : "Cuatro"} lecturas de tu libro, de una vez. Cada una se guarda en Word en la carpeta de tu libro; aquí ves el resumen.
            {largo ? ` Tu libro tiene ${palabras!.toLocaleString("es-ES")} palabras: los informes del editor, el lector beta, el estilo y el mercado leen ${soloElPrincipio}.` : ""}
          </p>
          <div className="lista-etapa">
            {d.piezas.map((p) => (
              <div className="fila-etapa" key={p.tipo}>
                <div className="que">
                  <b>{p.titulo}</b>
                  <span className="vacio">
                    {p.estado === "en_marcha" ? "leyendo tu libro, en segundo plano…"
                      : p.estado === "sin_leer" ? "no he podido leerlo: recarga la página; si sigue igual, mira que el motor esté activo"
                      : p.estado === "en_cola" ? "en cola: se hace en cuanto acabe el anterior"
                      : p.estado === "hecho" ? [nota(p.nota), p.recortado ? `leyó ${soloElPrincipio}` : "", p.resumen].filter(Boolean).join(" · ") || "hecho"
                      : p.estado === "anterior" ? `es de ${p.de}, una versión anterior: se hace de nuevo sobre el texto que tienes ahora`
                      : p.estado === "fallido" ? `no se pudo terminar${p.error ? `: ${p.error}` : ""}`
                      : p.que}
                  </span>
                </div>
                {p.estado === "hecho" && <span className="chip-estado ok">hecho</span>}
                {p.estado === "en_marcha" && <span className="chip-estado">en marcha</span>}
                {p.estado === "en_cola" && <span className="chip-estado">en cola</span>}
                {p.estado === "anterior" && <span className="chip-estado">de antes</span>}
                {/* El informe que ya existe se puede abrir siempre, también el de la versión anterior mientras
                    se rehace (equipo rojo, 3-oct: pagado y sin camino para verlo). */}
                {p.hay && p.tipo === "lectura" && <button className="btn texto" onClick={() => alIr("informe-lectura")}>{p.estado === "hecho" ? "Verlo" : "Ver el anterior"}</button>}
              </div>
            ))}
            {filaComparado}
            {/* M-2: el Word que lo junta todo, con el resumen delante. Solo el de la versión abierta. */}
            {word && (
              <div className="fila-etapa">
                <div className="que">
                  <b>{word.esWord ? "El diagnóstico entero, en un Word" : "El diagnóstico entero, en un documento de texto (este equipo no tiene el motor de Word)"}</b>
                  <span className="vacio">{nombreDeRuta(word.ruta)}{word.incompleto ? " · se hizo cuando aún faltaba alguna lectura: al completar el diagnóstico se rehace con todas" : " · el resumen delante y los informes detrás"}</span>
                </div>
                <button className="btn texto" onClick={() => { mostrarEnCarpeta(word.ruta).catch(() => {}); }}>Enseñar en la carpeta</button>
              </div>
            )}
          </div>
          {falloInforme && <p className="vacio" style={{ color: "var(--acc)" }}>{falloInforme}</p>}
          {cadenaRota && <p className="vacio" style={{ color: "var(--acc)" }}>El diagnóstico se paró: {cadenaRota}. Al volver a lanzarlo se pide solo lo que falta.</p>}
          {d.completo ? (
            <div className="pie-form">
              <p className="vacio">Hecho: las {d.piezas.length === 5 ? "cinco" : "cuatro"} lecturas son de esta versión. {word && !word.incompleto ? "Tienes el diagnóstico entero en un Word, y cada informe suelto, en la carpeta de tu libro." : "Los Word están en la carpeta de tu libro."}</p>
              <div className="botones">
                {/* Con el motor nuevo y sin su Word al día (los informes se hicieron sueltos): juntarlos no cuesta nada. */}
                {diag !== undefined && diag !== "sin_leer" && (!word || word.incompleto) && trabajoDiagnostico?.estado !== "en_curso" && (
                  <button className="btn fantasma" disabled={pidiendo === "word"} onClick={juntarEnWord}>{pidiendo === "word" ? "Juntándolo…" : "Juntarlo en un Word"}</button>
                )}
                <button className="btn" onClick={() => alIr("plan")}>Aprobar el diagnóstico y pasar al plan</button>
              </div>
            </div>
          ) : d.sinLeer || diag === "sin_leer" ? (
            // «No he podido leer» no es «no hay»: sin saber qué informes existen no se ofrece lanzar nada.
            <p className="vacio">No he podido leer qué informes tiene ya este libro, así que no te ofrezco hacerlos: podrías pagar dos veces el mismo. Recarga la página; si sigue igual, mira que el motor esté activo.</p>
          ) : (d.enMarcha > 0 || d.enCola > 0) && !d.faltan.length ? (
            <p className="vacio">Trabajando: {d.hechos} de {d.piezas.length} hechos{d.enCola > 0 ? `, ${d.enCola} en cola (se hacen de uno en uno)` : ""}. Puedes cerrar el Studio; sigue en tu ordenador{diag !== undefined ? " y, al acabar, lo junta todo en un Word" : ""}.</p>
          ) : (
            <div className="pie-form">
              <p className="vacio">
                {coste.calculado != null ? `~${dolares(coste.calculado)} de tu llave` : "Se paga con tu llave"}
                {cortos > 0 ? ` por ${cortos === 1 ? "un informe" : `los ${cortos} informes`}${largo ? ` (leen ${soloElPrincipio})` : " de libro entero"}` : ""}
                {coste.lectura === "medida" ? `, más el de lectura: en esta carpeta ha llegado a costar ${dolares(coste.lecturaMedida!)}`
                  : coste.lectura === "sin_medir" ? `${d.faltan.length > 1 ? ", más" : ":"} el de lectura, que va capítulo a capítulo y en un libro entero suele costar varios dólares` : ""}.
              </p>
              <button className="btn" disabled={!doc || lanzando || !d.faltan.length} onClick={() => hacerDiagnostico(d.faltan)}>
                {lanzando ? "Lanzando…" : d.hechos > 0 || d.enMarcha > 0 ? `Completar el diagnóstico (${d.faltan.length === 1 ? "falta 1" : `faltan ${d.faltan.length}`})` : "Hacer el diagnóstico"}
              </button>
            </div>
          )}
        </section>
      </>
    );
  }

  if (id === "diagnostico") {
    const hecho = !!informe;
    return (
      <>
        {cabecera}
        <section className="carta">
          <h2>Lo que tienes{doc ? <span className="sub">sobre {nombreDeRuta(doc)}</span> : null}</h2>
          {/* Rais, 21-sep: «no sé dónde se están grabando los ficheros de los informes; esto tiene
              que estar claro, para tontos». Dicho arriba, con la carpeta entera y un botón para copiarla. */}
          {carpeta && <DondeSeGuardan carpeta={carpeta} />}
          {caduca && <AvisoCaduca c={caduca} />}
          <div className="lista-etapa">
            <div className="fila-etapa">
              <div className="que">
                <b>Informe de lectura</b>
                <span className="vacio">
                  {trabajoInforme?.estado === "en_curso" ? "leyendo el libro entero, en segundo plano…"
                    : informe === undefined ? "no he podido leerlo: recarga la página; si sigue igual, mira que el motor esté activo"
                    : hecho && leidaOtra ? `leyó ${leidaOtra}, y tu manuscrito ya es ${nombreDeRuta(doc!)}: pídelo otra vez para que hable del texto que tienes ahora`
                    : hecho ? `${etapa.detalle} · ${informe?.chapterCount ?? 0} capítulos`
                    : trabajoInforme?.estado === "fallido" ? `no se pudo terminar: ${trabajoInforme.error}`
                    : "sin hacer"}
                </span>
              </div>
              {hecho && (leidaOtra ? <span className="chip-estado" title={`El informe se hizo sobre ${leidaOtra}`}>de una versión anterior</span>
                : cadLectura ? <span className="chip-estado" title={cadLectura.que}>de antes de la {cadLectura.version}</span>
                : <span className="chip-estado ok">hecho</span>)}
              {hecho && <button className="btn texto" onClick={() => alIr("informe-lectura")}>Verlo</button>}
              {/* Mientras se lee, ningún botón: un «Pedirlo» apagado al lado de «leyendo…» confunde
                  (Rais, 21-sep). El estado ya lo dice la línea de la izquierda. */}
              {trabajoInforme?.estado === "en_curso" || pidiendo === "lectura"
                ? <span className="chip-estado">en marcha</span>
                : <button className={hecho ? "btn texto" : "btn fantasma"} disabled={!doc}
                          onClick={pedirLectura}>{leidaOtra ? "Leer la versión nueva" : hecho ? "Rehacer" : "Pedirlo"}</button>}
            </div>
            {([
              ["editor", "Informe del editor", "editor", "Hazme el informe de desarrollo de mi manuscrito: estructura, personajes, ritmo y qué cambiar primero."],
              ["beta", "Lector beta", "coach", "Léete mi manuscrito como un lector beta de mi público y dime qué siente, dónde se aburre y dónde se engancha."],
              ["estilo", "Estilo", "coach", "Analiza el estilo de mi manuscrito con los diez ejes: qué tengo, qué me falta y qué no tocar."],
              ["mercado", "Adecuación al mercado", "coach", "Dime cómo encaja mi manuscrito en el mercado: tendencias, competencia, palabras clave, BISAC, potencial de ventas y editoriales."],
            ] as const).filter(([tipo]) => tipo !== "mercado" || fichas === undefined || tiposDeInforme == null || tiposDeInforme.includes(tipo)).map(([tipo, t, ag, enc]) => {
              const ficha = fichas?.[tipo];
              const vivo = ultimoTrabajo("informe-" + tipo);
              const coste = palabras != null ? costeInforme(palabras, precios[ag]) : null;
              return (
                <div className="fila-etapa" key={tipo}>
                  <div className="que">
                    <b>{t}</b>
                    <span className="vacio">
                      {vivo?.estado === "en_curso" ? "leyendo el libro entero, en segundo plano…"
                        : ficha ? `${ficha.nota != null ? `nota ${String(ficha.nota).replace(".", ",")} · ` : ""}${new Date(ficha.fecha).toLocaleDateString("es-ES", { day: "numeric", month: "short" })} · ${ficha.version}${ficha.recortado ? " · leyó solo las primeras ~83.000 palabras" : ""}${ficha.resumen ? ` · ${ficha.resumen}` : ""}${ficha.ruta ? ` · En tu carpeta: ${nombreDeRuta(ficha.ruta)}` : ""}`
                        : fichas === undefined ? "con un motor anterior a la 1.6.3 se pide en el chat"
                        : vivo?.estado === "fallido" ? `no se pudo terminar: ${vivo.error}`
                        : `${palabras && palabras > PALABRAS_MAX_LIBRO ? `las primeras ~${PALABRAS_MAX_LIBRO.toLocaleString("es-ES")} de ${palabras.toLocaleString("es-ES")} palabras` : `libro entero${palabras ? ` · ~${palabras.toLocaleString("es-ES")} palabras` : ""}`}${coste != null ? ` · ~${dolares(coste)} de tu llave` : ""}`}
                    </span>
                  </div>
                  {ficha && (cadFicha(tipo) ? <span className="chip-estado" title={cadFicha(tipo)!.que}>de antes de la {cadFicha(tipo)!.version}</span> : <span className="chip-estado ok">hecho</span>)}
                  {fichas === undefined
                    ? <button className="btn fantasma" onClick={() => alHablar(ag, enc)}>Pedirlo en el chat</button>
                    : vivo?.estado === "en_curso" || pidiendo === tipo
                      ? <span className="chip-estado">en marcha</span>
                      : <button className={ficha ? "btn texto" : "btn fantasma"} disabled={!doc}
                                onClick={() => pedir(tipo)}>{ficha ? "Rehacer" : "Pedirlo"}</button>}
                </div>
              );
            })}
            {filaComparado}
          </div>
          {falloInforme && <p className="vacio" style={{ color: "var(--acc)" }}>{falloInforme}</p>}
        </section>
        {/* M-14: el tablero de notas, dentro del Diagnóstico: lo que te han dicho del libro es lo
            que se trabaja. El informe de lectura entero se lee en su sección (botón «Verlo»). */}
        {!flujo && <Tablero carpeta={carpeta} doc={doc} pulso={pulso} alDecidir={() => alCambio?.()} alLanzar={() => alLanzar?.()} />}
        {/* 25-sep: la biblia, la escaleta y la guía de estilo, sacadas del manuscrito con la plantilla
            de la skill (encargos del motor), en Word en la carpeta. Antes solo había «pídesela al coach». */}
        {carpeta && !flujo && <Encargos carpeta={carpeta} doc={doc} ids={["biblia", "escaleta", "guia-estilo"]} trabajos={trabajos} pulso={pulso}
                              alLanzar={() => alLanzar?.()} titulo="Los documentos de tu libro" sub="sacados de tu manuscrito" />}
      </>
    );
  }

  if (id === "plan") {
    return (
      <>
        {cabecera}
        {/* En el flujo, el plan empieza por las escenas: se escribe sobre ellas, por su número. */}
        {flujo && planSoportado && (
          <Escenas carpeta={carpeta} doc={doc} trabajo={ultimoTrabajo("escenas")} pulso={pulso} conPlan={!!plan}
                   alLanzar={() => alLanzar?.()} alSaber={setEscenasListas} />
        )}
        <PlanDelLibro plan={plan} soportado={planSoportado} planEnCarpeta={planEnCarpeta} carpeta={carpeta} doc={doc}
                      trabajo={ultimoTrabajo("plan")} pasadaEnMarcha={ultimoTrabajo("pasada")?.estado === "en_curso" || ultimoTrabajo("fase")?.estado === "en_curso"} alLanzar={() => alLanzar?.()} alCambio={() => alCambio?.()}
                      alHablar={alHablar} encargo={h.encargo} faltan={faltan} alIr={alIr} caduca={caduca}
                      faltanEscenas={flujo && escenasListas === false} />
      </>
    );
  }

  if (id === "pasadas") {
    // Una pasada del plan puede correr como «pasada» o, si va por operaciones sobre escenas, como
    // «operaciones»: las dos crean una versión nueva (Rais, 23-sep: la v02 estaba hecha y el Studio
    // seguía en la v01 porque solo miraba una de las dos).
    const vivo = ultimoTrabajo("pasada") ?? ultimoTrabajo("operaciones");
    // EL FLUJO HACE LA FASE ENTERA, si el motor sabe (M-5). Con un motor anterior —o si el autor
    // prefiere ir de una en una—, la tarjeta de siempre.
    const porFases = flujo && fasePrevia !== undefined && !deUnaEnUna;
    return (
      <>
        {cabecera}
        {porFases && (
          <EjecutarFase previa={fasePrevia ?? null} sinLeer={faseSinLeer && !fasePrevia} alReintentar={leerFase} carpeta={carpeta} doc={doc} trabajo={trabajoFase} precio={precios.editor}
                        alLanzar={() => alLanzar?.()} alSeguirCon={alSeguirCon} alHablar={() => alHablar(h.agente, h.encargo)} alIr={alIr}
                        alIrDeUnaEnUna={() => setDeUnaEnUna(true)} />
        )}
        {flujo && deUnaEnUna && fasePrevia !== undefined && (
          <p className="vacio"><button className="btn texto" onClick={() => setDeUnaEnUna(false)}>← Volver a hacer la fase entera</button></p>
        )}
        {!porFases && <EjecutarPasada siguiente={siguiente ?? null} pendientes={pendientes} base={plan?.manuscrito_base ?? null} total={pasadasPlan.filter((p) => p.estado !== "saltada").length} soportado={planSoportado && !!plan} carpeta={carpeta} doc={doc}
                        trabajo={vivo} precio={precios.editor} alLanzar={() => alLanzar?.()} alSeguirCon={alSeguirCon}
                        alHablar={() => alHablar(h.agente, flujo ? h.encargo : siguiente
                          ? `Vamos con la pasada ${siguiente.n} del plan: «${siguiente.titulo}». Dime qué vas a cambiar antes de hacerlo.`
                          : h.encargo)}
                        alIr={alIr} />}
        <Pasadas carpeta={carpeta} doc={doc} pulso={pulso} trabajo={trabajoMedir} alLanzar={alLanzar} alCambiarDoc={alCambiarDoc} alCambio={alCambio} />
        <section className="carta">
          <h2>Registro de la reescritura<span className="sub">en Word, sin gastar</span></h2>
          <p className="vacio">
            Versión a versión: qué se cambió, en qué capítulo, por qué nota y qué hiciste tú con cada cambio en Word; y al
            final, lo que queda pendiente. Sale de lo que el Studio ya ha ido guardando{flujo ? "" : ", que es lo mismo que cuenta tu certificado"}.
          </p>
          <div className="botones">
            <button className="btn fantasma" disabled={!doc || registro?.haciendo} onClick={hacerElRegistro}>
              {registro?.haciendo ? "Haciéndolo…" : registro?.ruta ? "Hacerlo otra vez" : "Hacer el registro"}
            </button>
          </div>
          {registro?.ruta && <p className="vacio">Hecho. Está en tu carpeta: <b className="mono">{registro.ruta}</b></p>}
          {registro?.error && <p className="vacio" style={{ color: "var(--acc)" }}>{registro.error}</p>}
        </section>
      </>
    );
  }

  if (id === "correccion") {
    return (
      <>
        {cabecera}
        <Corregir doc={doc} carpeta={carpeta} soportado={planSoportado} trabajo={ultimoTrabajo("correccion")} ultimaHecha={trabajos.find((t) => t.tipo === "correccion" && t.estado === "hecho") ?? null} alLanzar={() => alLanzar?.()}
                  precio={precios.copyeditor} alCorregir={alCorregir} corrigiendo={corrigiendo} alHablar={() => alHablar(h.agente, h.encargo)} />
      </>
    );
  }

  // PUBLICACIÓN EN DOS CAMINOS (Studio nuevo, 3-oct-2026): tradicional o por tu cuenta, cada uno de
  // golpe. Debajo, el libro maquetado y la cubierta; y, plegados, todos los encargos uno a uno.
  if (flujo) {
    return (
      <>
        {cabecera}
        {carpeta && <Publicar carpeta={carpeta} doc={doc} trabajos={trabajos} pulso={pulso} alLanzar={() => alLanzar?.()} />}
        <Maquetar carpeta={carpeta} doc={doc} trabajo={ultimoTrabajo("maquetacion")} alLanzar={() => alLanzar?.()} />
        {carpeta && <Cubierta carpeta={carpeta} doc={doc} trabajo={ultimoTrabajo("wrap")} pulso={pulso} alLanzar={() => alLanzar?.()} />}
        {carpeta && (
          <details className="plegable">
            <summary><h2 style={{ display: "inline" }}>Todos los encargos<span className="sub"> uno a uno</span></h2></summary>
            <Encargos carpeta={carpeta} doc={doc} etapa="publicacion" trabajos={trabajos} pulso={pulso}
                      alLanzar={() => alLanzar?.()} titulo="Lo que puedes encargar" sub="cada uno se guarda en Word en tu carpeta" />
          </details>
        )}
      </>
    );
  }

  // Publicación. Los encargos son los modos reales de las skills (25-sep): antes eran cinco textos
  // escritos de memoria que abrían el chat y no guardaban nada.
  return (
    <>
      {cabecera}
      <Maquetar carpeta={carpeta} doc={doc} trabajo={ultimoTrabajo("maquetacion")} alLanzar={() => alLanzar?.()} />
      {carpeta && <Encargos carpeta={carpeta} doc={doc} etapa="publicacion" trabajos={trabajos} pulso={pulso}
                            alLanzar={() => alLanzar?.()} titulo="Lo que puedes encargar" sub="cada uno se guarda en Word en tu carpeta" />}
      {/* 26-sep: el flujo de Cowork — la portada que eliges en otra IA y el motor monta la cubierta para KDP. */}
      {carpeta && <Cubierta carpeta={carpeta} doc={doc} trabajo={ultimoTrabajo("wrap")} pulso={pulso} alLanzar={() => alLanzar?.()} />}
    </>
  );
}

/** Dónde caen los informes: la carpeta del proyecto, entera y copiable. */
function DondeSeGuardan({ carpeta }: { carpeta: string }) {
  const [copiado, setCopiado] = useState(false);
  return (
    <div className="aviso" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
      <span>
        Todos los informes se guardan <b>en Word</b> en la carpeta de tu libro:{" "}
        <b className="mono">{carpeta}</b>
      </span>
      <button className="btn texto" onClick={() => {
        navigator.clipboard?.writeText(carpeta).then(() => { setCopiado(true); setTimeout(() => setCopiado(false), 2000); }).catch(() => {});
      }}>{copiado ? "Copiada" : "Copiar la carpeta"}</button>
    </div>
  );
}
